import React, { useState } from 'react';
import Header from '../components/Header/Header';
import Navbar from '../components/Navbar/Navbar';
import logo from "../components/assets/im_logo.png";
import Loader from "@mui/material/CircularProgress";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const OtpPage = () => {
  // Define the state for OTP boxes (6 input fields)
  const API_URL = process.env.REACT_APP_API_URL
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [otpBoxes, setOtpBoxes] = useState(["", "", "", "", "", ""]);
  const email = sessionStorage.getItem("email")
  if(!email) {
    navigate("/register")
  }
  const{mutate, isPending} = useMutation({
    mutationFn: async ({ email, otp }) => {
        const res = await fetch(`${API_URL}/auth/verify-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email, otp }),
        });
      
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Something went wrong");
        return data;
      },
    onSuccess: () => {
        toast.success("Otp verified successfully")
        queryClient.invalidateQueries({queryKey: ['authUser']})
    },
    onError: (error) => {
        toast.error(error.message)
    }
  })
  const {mutate: resendMutation, isPending: isResending} = useMutation({
    mutationFn: async () => {
        const response = await fetch(`${API_URL}/auth/resend-otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          
          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.message || 'Failed to resend OTP');
          }
          const data = await response.json()
          return data
    },
    onSuccess: (data) => {
        toast.success(data.message)
    },
    onError: (err) => {
        toast.error(err.message)
    }
  })
  // Handle changes in OTP input fields
  const handleOtpChange = (e, index) => {
    const value = e.target.value;

    if (/[^0-9]/.test(value) && value !== "") return; 

    const newOtpBoxes = [...otpBoxes];
    newOtpBoxes[index] = value;

    // If the user typed a value, move focus to the next box
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`).focus();
    }

    setOtpBoxes(newOtpBoxes);
  };

  // Handle backspace to delete previous OTP value and move focus back
  const handleBackspace = (e, index) => {
    if (e.key === 'Backspace' && otpBoxes[index] === "") {
      // Move focus to the previous box and delete its value
      if (index > 0) {
        const newOtpBoxes = [...otpBoxes];
        newOtpBoxes[index - 1] = ""; 
        setOtpBoxes(newOtpBoxes);
        document.getElementById(`otp-${index - 1}`).focus(); 
      }
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    const otp = otpBoxes.join("");
    if (otp.length !== 6) {
      toast.error("Please enter a 6-digit OTP");
      return;
    }
    mutate({ email, otp });
  };
  const handleResendOtp = (e) => {
    e.preventDefault()
    if(isResending) return;
    resendMutation()
  }
  return (
    <>
      <Header />
      <Navbar />
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-4 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <img alt="Your Company" src={logo} className="mx-auto h-24 w-auto" />
          <h2 className="mt-10 text-center text-2xl/9 font-medium tracking-tight text-gray-900">
            Enter the OTP sent to your email 
          </h2>
          <p className='text-md text-center'>{email}</p>
        </div>

        <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-sm">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex justify-center gap-2">
              {/* OTP Inputs */}
              {otpBoxes.map((box, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  maxLength="1"
                  className="w-10 h-10 text-center text-2xl font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-indigo-600"
                  value={box}
                  onChange={(e) => handleOtpChange(e, index)}
                  onKeyDown={(e) => handleBackspace(e, index)}
                />
              ))}
            </div>

            <div className="mt-6">
              <button
                type="submit"
                className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                disabled={isPending}
              >
                {isPending ?<Loader sx={() => ({ color: "#ffffff" })} size={24}/> : "Verify OTP"}
              </button>
            </div>
          </form>

          <p className="mt-10 text-center text-sm/6 text-gray-500">
            Didn't receive the OTP?{" "}
            <a
              disabled={isResending}
              onClick={handleResendOtp}
              className="font-semibold text-indigo-600 cursor-pointer hover:text-indigo-500"
            >
              {isResending ? "Resending..." : "Resend OTP"}
            </a>
          </p>
        </div>
      </div>
    </>
  );
}

export default OtpPage;
